import { CheckCheck, Undo2 } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { useBatchExpiry } from "../../hooks/useBatchExpiry";
import type { PayoutBatchDetail } from "../../types/payoutBatches.types";

/**
 * Who is looking decides the actions: an approver who did not make the batch approves or sends it back; its
 * maker may take it back to edit it; anyone else only reads.
 */
export type ApprovalViewer = "approver" | "maker" | "reader";

export interface BatchApprovalFooterProps {
  batch: PayoutBatchDetail;
  viewer: ApprovalViewer;
  /** The maker can take it back only with `payout_batches:write`. */
  canWithdraw: boolean;
  onApprove: () => void;
  onReject: () => void;
  onWithdraw: () => void;
  isWithdrawing: boolean;
}

type ApproveBlock = "expired" | "insufficient" | "blockers" | null;

const approveBlock = (batch: PayoutBatchDetail, isExpired: boolean): ApproveBlock => {
  if (isExpired) return "expired";
  if (batch.summary.wallet && !batch.summary.wallet.sufficient) return "insufficient";
  if (batch.summary.blockers.length > 0 || !batch.summary.can_execute) return "blockers";
  return null;
};

const BLOCK_KEYS = {
  expired: "footer_blocked_expired",
  insufficient: "footer_blocked_insufficient",
  blockers: "footer_blocked_blockers",
} as const;

/** The sticky ticket of a batch waiting for its second user. The selection is frozen: nothing here edits it. */
export function BatchApprovalFooter({
  batch,
  viewer,
  canWithdraw,
  onApprove,
  onReject,
  onWithdraw,
  isWithdrawing,
}: BatchApprovalFooterProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const { isExpired } = useBatchExpiry(batch.expires_at);
  const block = approveBlock(batch, isExpired);
  const source = batch.source_currency;

  const hint =
    viewer === "approver"
      ? block
        ? t(BLOCK_KEYS[block])
        : t("approval_footer_approver_hint")
      : viewer === "maker"
        ? t("approval_footer_maker_hint")
        : t("approval_footer_reader_hint");

  return (
    <div className="sticky bottom-0 z-10 -mx-2 border-t border-border bg-card/95 px-4 py-3 shadow-[var(--shadow-elev)] backdrop-blur supports-[backdrop-filter]:bg-card/80 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm text-foreground tabular-nums">
            {t("footer_summary", {
              count: batch.counters.selected,
              debit: `${formatCurrencyAmount(batch.totals.debit_indicative_selected, source, activeLanguage)} ${source}`,
            })}
          </p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {viewer === "approver" && (
            <>
              <ColocaButton variant="default" icon={Undo2} onClick={onReject}>
                {t("action_reject")}
              </ColocaButton>
              <ColocaButton variant="primary" icon={CheckCheck} disabled={block !== null} onClick={onApprove}>
                {t("action_approve")}
              </ColocaButton>
            </>
          )}
          {viewer === "maker" && canWithdraw && (
            <ColocaButton variant="default" icon={Undo2} loading={isWithdrawing} onClick={onWithdraw}>
              {t("action_withdraw")}
            </ColocaButton>
          )}
        </div>
      </div>
    </div>
  );
}
